import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { suppliers as suppliersApi } from "@/api";
import http from "@/api";
import { FileText, Download, GraduationCap, Info, TrendingUp, Eye, Lightbulb, Compass, ArrowUpRight } from "lucide-react";
import PillarRadar from "@/components/PillarRadar";
import RecognitionPanel from "@/components/RecognitionPanel";
import TierBadge from "@/components/TierBadge";
import GemBadge from "@/components/GemBadge";
import DigitalSeal from "@/components/DigitalSeal";
import CategoryBenchmark from "@/components/CategoryBenchmark";
import BusinessImpactPanel from "@/components/BusinessImpactPanel";
import { PILLARS, TIER_META, TIER_KEYS, TIER_BADGE_IMG, normalizeTier } from "@/constants";
import { useAuth } from "@/auth";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const PILLAR_ADVICE = {
  calidad: "Cuidemos juntos las certificaciones vigentes y compartamos los hallazgos de auditoría apenas surjan. Cero sorpresas = confianza alta.",
  costo: "Acompañemos los ajustes con datos del mercado y propuestas de eficiencia compartida. La transparencia abre puertas.",
  continuidad: "Cumplir el lead-time prometido y mantener inventario activo son las palancas que más impacto generan en la familia Lala.",
  innovacion: "Compártenos tus ideas proactivamente — sea producto, proceso o digitalización. Las mejores innovaciones nacen de la conversación.",
  sustentabilidad: "Documenta tus avances ESG, plan de reducción de huella y economía circular. Aquí sumar un punto es muy alcanzable.",
  relacion: "Documentación al día y respuesta clara cuando Lala te necesita. Tu cercanía construye relación de largo plazo.",
};

const NEXT_TIER = { commercial: "challenge", challenge: "key", key: "partner", partner: null };

export default function MyEvaluation() {
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [history, setHistory] = useState(null);
  const [badge, setBadge] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [flags, setFlags] = useState({});
  const [reportPolicy, setReportPolicy] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [requestMsg, setRequestMsg] = useState("");

  useEffect(() => {
    if (!user?.supplier_id) return;
    Promise.all([
      suppliersApi.get(user.supplier_id),
      suppliersApi.history(user.supplier_id),
      suppliersApi.badge(user.supplier_id),
    ]).then(([s, h, b]) => { setSupplier(s); setHistory(h); setBadge(b); });
    http.get("/program/supplier-flags").then((r) => setFlags(r.data?.flags || {})).catch(() => {});
    http.get(`/suppliers/${user.supplier_id}/benchmark`).then((r) => setBenchmark(r.data)).catch(() => {});
    http.get("/program/report-request-policy").then((r) => setReportPolicy(r.data)).catch(() => {});
    http.get("/supplier/my-report-requests").then((r) => setMyRequests(r.data?.items || [])).catch(() => {});
  }, [user]);

  const lastRequest = myRequests[0];
  const policyMode = reportPolicy?.mode || "free";
  const needsRequest = ["approval", "paid"].includes(policyMode);
  const hasApproved = (myRequests || []).some((r) => r.status === "approved");
  const hasPending = (myRequests || []).some((r) => r.status === "pending");
  const requestReport = async () => {
    setRequesting(true); setRequestMsg("");
    try {
      await http.post("/supplier/request-report", { note: "" });
      const { data } = await http.get("/supplier/my-report-requests");
      setMyRequests(data?.items || []);
      setRequestMsg("Solicitud enviada. Lala te avisa cuando se autorice.");
    } catch (e) {
      setRequestMsg("No se pudo enviar: " + (e.response?.data?.detail || e.message));
    } finally { setRequesting(false); }
  };

  const latest = supplier?.latest_evaluation;
  const trend = useMemo(() => (history?.evaluations || []).map((e) => ({ period: e.period, score: e.score })), [history]);

  // Recommendations: lowest 2 pillars
  const recommendations = useMemo(() => {
    if (!latest?.pillars) return [];
    return PILLARS
      .map((p) => ({ ...p, value: latest.pillars[p.key] ?? 0 }))
      .sort((a, b) => a.value - b.value)
      .slice(0, 2);
  }, [latest]);

  const tierKey = normalizeTier(supplier?.current_tier);
  const tierMeta = TIER_META[tierKey];
  const nextKey = NEXT_TIER[tierKey];
  const nextMeta = nextKey ? TIER_META[nextKey] : null;
  const currentScore = supplier?.current_score;

  if (user && user.role !== "supplier") return <Navigate to="/dashboard" replace />;
  if (!user?.supplier_id) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="font-display text-3xl">Tu cuenta aún no está vinculada</div>
        <p className="text-[var(--ink-soft)] mt-2">Pide al equipo Lala que vincule tu proveedor para consultar tu desempeño.</p>
      </div>
    );
  }
  if (!supplier) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando tu desempeño…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="my-evaluation-page">
      {/* HERO — vista de solo lectura */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 sketch-border bg-[var(--cream-soft)] p-7" data-testid="my-eval-header">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[var(--lala-blue)] mb-2">
            <Eye size={12} /> Vista de consulta · Fase 1
          </div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">hola, {user.name.split(" ")[0]}</div>
          <h1 className="font-display text-4xl mt-1">{supplier.name}</h1>
          <p className="font-hand text-2xl text-[var(--lala-blue)] mt-2">Bienvenido a tu espacio en Lala Elite — crezcamos juntos.</p>
          <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed max-w-2xl">
            Aquí encuentras tu desempeño dentro del programa, cómo se compara con tu categoría y los pasos para
            seguir avanzando. <b>Lala alimenta los datos</b> para asegurar consistencia y trazabilidad — tu rol es
            consultar, entender y crecer.
          </p>
          {/* Datasource badge */}
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] bg-white border border-[var(--line)] rounded-full px-3 py-1" data-testid="data-source-badge">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[var(--ink-soft)]">Fuente de tu evaluación:</span>
            <strong className="text-[var(--ink)]">
              "Evaluación 360° del equipo Lala"
            </strong>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Tu score</div>
              <div className="font-display text-5xl text-[var(--lala-blue)]" data-testid="my-score">
                {currentScore != null ? (currentScore * 10).toFixed(1) : "—"}
                {currentScore != null && <span className="text-xl text-[var(--ink-soft)]"> /100</span>}
              </div>
              {currentScore != null && (
                <div className="text-xs text-[var(--ink-soft)] mt-0.5" data-testid="my-score-10">{currentScore.toFixed(2)} en escala 0–10</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Nivel actual</div>
              <div className="mt-1"><TierBadge tier={supplier.current_tier} size="lg" /></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Período</div>
              <div className="font-display text-2xl">{supplier.last_period || "—"}</div>
            </div>
          </div>
          {flags.supplier_report_download_enabled !== false && (
            <div className="mt-6 space-y-2" data-testid="report-block">
              {(!needsRequest || hasApproved) && (
                <button
                  onClick={async () => {
                    try {
                      const res = await http.get(`/suppliers/${user.supplier_id}/executive-report`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `Lala_Elite_Reporte_${(supplier.name || "proveedor").replace(/\s+/g, "_")}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      window.alert("No se pudo generar el reporte: " + (e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="download-my-report-btn"
                  className="sketch-pill bg-[var(--lala-red,#E0162B)] text-[var(--cream)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
                >
                  <FileText size={14} /> Descargar mi reporte mensual (PDF) <Download size={14} />
                </button>
              )}
              {needsRequest && !hasApproved && (
                <div data-testid="request-report-block">
                  <button
                    onClick={requestReport}
                    disabled={requesting || hasPending}
                    data-testid="request-report-btn"
                    className="sketch-pill bg-[var(--lala-blue)] text-white px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50"
                  >
                    {hasPending ? "Solicitud enviada · esperando a Lala" : "Solicitar mi reporte a Lala"}
                  </button>
                  <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-xl leading-snug">
                    {policyMode === "paid"
                      ? `Lala definió un costo de $${reportPolicy?.price_mxn?.toLocaleString?.("es-MX") || "—"} MXN por la entrega del reporte físico/PDF. ${reportPolicy?.message_to_supplier || ""}`
                      : (reportPolicy?.message_to_supplier || "Solicítalo al equipo Lala. Te avisamos cuando se autorice.")
                    }
                  </p>
                  {lastRequest && (
                    <p className="text-[11px] text-[var(--ink-soft)] mt-1">
                      Última solicitud: <b className="text-[var(--ink)]">{new Date(lastRequest.created_at).toLocaleDateString("es-MX")}</b> · estado: <b className={lastRequest.status === "approved" ? "text-emerald-700" : lastRequest.status === "denied" ? "text-red-700" : "text-amber-700"}>{lastRequest.status}</b>
                    </p>
                  )}
                  {requestMsg && <p className="text-xs text-[var(--lala-blue)] mt-1">{requestMsg}</p>}
                </div>
              )}
            </div>
          )}
        </div>
        <div>{badge && <DigitalSeal badge={badge} />}</div>
      </div>

      {/* BENCHMARK DE CATEGORÍA + DISTINTIVO */}
      <CategoryBenchmark benchmark={benchmark} />

      {/* RECONOCIMIENTO 1-4 + CAMINO DE MEJORA */}
      <div className="mb-6">
        <RecognitionPanel supplierId={user.supplier_id} />
      </div>

      {/* RACIONAL DEL EVALUADOR — texto que Lala captura al cerrar tu período */}
      {latest?.notes && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="my-eval-rationale">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Info size={18} /> racional de tu evaluación
          </div>
          <h3 className="font-display text-2xl mb-2">Notas del equipo Lala sobre tu período {supplier.last_period}</h3>
          <p className="text-sm text-[var(--ink)] leading-relaxed whitespace-pre-line">{latest.notes}</p>
        </div>
      )}

      {/* RADAR Y PILARES */}
      {latest && (
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="sketch-border bg-[var(--cream-soft)] p-6">
            <div className="font-hand text-xl text-[var(--lala-blue)]">así vas en tus pilares</div>
            <h3 className="font-display text-2xl mb-2">Tu radar 360°</h3>
            <PillarRadar pillars={latest.pillars} />
          </div>
          <div className="sketch-border bg-[var(--cream-soft)] p-6">
            <div className="font-hand text-xl text-[var(--lala-blue)]">detalle por pilar</div>
            <h3 className="font-display text-2xl mb-3">Tu score, pilar por pilar</h3>
            <div className="space-y-3">
              {PILLARS.map((p) => {
                const v = latest.pillars[p.key] ?? 0;
                return (
                  <div key={p.key} data-testid={`pillar-row-${p.key}`}>
                    <div className="flex justify-between items-baseline text-sm font-semibold">
                      <span>{p.label}</span>
                      <span className="text-[var(--lala-blue)] font-display text-lg">{v.toFixed(2)}</span>
                    </div>
                    <div className="h-2.5 mt-1 rounded-full bg-[var(--cream-deep)]">
                      <div className="h-2.5 rounded-full" style={{ width: `${(v / 10) * 100}%`, background: "var(--lala-blue)" }} />
                    </div>
                    <p className="text-[11px] text-[var(--ink-soft)] mt-1 leading-snug">{p.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* EVOLUCIÓN */}
      {trend.length > 1 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="my-eval-trend">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <TrendingUp size={18} /> tu evolución dentro de Lala Elite
          </div>
          <h3 className="font-display text-2xl mb-2">Vas en camino. Seguimos juntos.</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height={280} minWidth={200} minHeight={160}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
                <XAxis dataKey="period" stroke="#11142B" />
                <YAxis domain={[5, 10]} stroke="#11142B" />
                <Tooltip contentStyle={{ background: "#FBF7EE", border: "1.5px solid #0033AD", borderRadius: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#0033AD" strokeWidth={3} dot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* RECOMENDACIONES — NO accionables dentro del sistema */}
      {recommendations.length > 0 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="my-recommendations">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Lightbulb size={18} /> donde podemos crecer juntos
          </div>
          <h3 className="font-display text-2xl mb-3">Tus oportunidades más cercanas</h3>
          <p className="text-sm text-[var(--ink-soft)] mb-4 max-w-3xl">
            Estas son las áreas donde tu siguiente paso tiene mayor impacto. El equipo Lala las usa
            para acompañarte — no necesitas cargar nada en la plataforma, solo conversemos sobre ellas.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {recommendations.map((p) => (
              <div key={p.key} className="bg-white rounded-xl p-4 border border-[var(--line)]" data-testid={`recommendation-${p.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-display text-lg text-[var(--lala-blue)]">{p.label}</div>
                  <div className="text-xs text-[var(--ink-soft)]">score actual <b className="text-[var(--ink)]">{p.value.toFixed(2)}</b></div>
                </div>
                <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{PILLAR_ADVICE[p.key]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CAMINO HACIA EL SIGUIENTE NIVEL */}
      {nextMeta && currentScore != null && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="next-tier-path">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Compass size={18} /> camino hacia el siguiente nivel
          </div>
          <h3 className="font-display text-2xl mb-3">
            De <span style={{ color: tierMeta?.color }}>{tierMeta?.humanLabel}</span> a <span style={{ color: nextMeta.color }}>{nextMeta.humanLabel}</span>
          </h3>
          <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
            <span className="px-3 py-1 rounded-full text-white font-semibold" style={{ background: tierMeta?.color }}>{tierMeta?.humanLabel} · {tierMeta?.range}</span>
            <GemBadge tier={tierKey} size="sm" variant="pill" />
            <ArrowUpRight size={18} className="text-[var(--ink-soft)]" />
            <span className="px-3 py-1 rounded-full text-white font-semibold" style={{ background: nextMeta.color }}>{nextMeta.humanLabel} · {nextMeta.range}</span>
            <GemBadge tier={nextKey} size="sm" variant="pill" />
          </div>
          <p className="text-sm text-[var(--ink-soft)] leading-relaxed max-w-3xl">{nextMeta.description}</p>
          <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-3xl">
            <b>¿Cómo se llega?</b> Subir consistentemente los pilares con menor puntaje y mantener los altos. Lala te
            acompaña con feedback mensual y contenidos del Centro de Aprendizaje.
          </p>
        </div>
      )}

      {/* CÓMO SE CALCULA */}
      <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="how-score-works">
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <Info size={18} /> cómo construimos tu evaluación juntos
        </div>
        <h3 className="font-display text-2xl mb-3">Tu score, sin cajas negras</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <Step n="1" title="El equipo Lala te evalúa">
            Cada pilar se evalúa dentro del <b>programa LALA Elite</b>: los especialistas de cada pilar
            (calidad, costo, continuidad, innovación, sustentabilidad y nivel de servicio) registran tu
            desempeño con la misma metodología para todos los proveedores.
          </Step>
          <Step n="2" title="Calculamos tu nivel">
            Cada pilar tiene un peso configurado por Lala. Tu score global es el promedio ponderado de los 6
            pilares — la misma fórmula para todos los proveedores de tu categoría.
          </Step>
          <Step n="3" title="Lala valida y comparte">
            El administrador del programa revisa y aprueba cada calificación antes de publicarla.
            Solo entonces tu score aparece aquí, listo para conversarlo juntos.
          </Step>
        </div>
      </div>

      {/* NIVELES */}
      <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="tiers-explainer">
        <div className="font-hand text-xl text-[var(--lala-blue)]">los 4 niveles del programa</div>
        <h3 className="font-display text-2xl mb-3">Qué significa cada nivel</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TIER_KEYS.map((k) => {
            const m = TIER_META[k];
            const isCurrent = k === tierKey;
            return (
              <div key={k} className={`rounded-xl p-4 border ${isCurrent ? "border-[var(--lala-blue)] bg-white shadow-sm" : "border-[var(--line)] bg-white/60"}`}
                   data-testid={`tier-explainer-${k}`}>
                <img src={TIER_BADGE_IMG[k]} alt={`Insignia ${m.humanLabel}`} loading="lazy"
                     className="w-20 h-20 object-contain mx-auto mb-2 drop-shadow-sm"
                     data-testid={`tier-badge-img-${k}`} />
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg" style={{ color: m.color }}>{m.humanLabel}</div>
                  {isCurrent && <span className="text-[9px] uppercase tracking-wider font-bold text-[var(--lala-blue)]">Tu nivel</span>}
                </div>
                <GemBadge tier={k} size="xs" variant="minimal" className="mt-0.5" />
                <div className="text-[11px] text-[var(--ink-soft)] mb-1">{m.range}</div>
                <p className="text-xs text-[var(--ink-soft)] leading-snug">{m.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* APRENDIZAJE CTA */}<Link
        to="/aprendizaje"
        data-testid="learning-cta"
        className="block sketch-border bg-[var(--lala-blue)] text-white p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform mb-6"
      >
        <div className="flex items-center gap-4">
          <GraduationCap size={32} />
          <div className="flex-1">
            <div className="font-hand text-xl opacity-90">crecemos contigo</div>
            <div className="font-display text-2xl">Centro de Aprendizaje · videos, guías y FAQ</div>
            <div className="text-sm opacity-90 mt-1">Conoce qué significa cada pilar, cada etapa del camino y cómo seguimos avanzando como familia Lala.</div>
          </div>
          <ArrowUpRight size={28} />
        </div>
      </Link>

      <div className="mb-6">
        <BusinessImpactPanel />
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-[var(--line)]">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-7 h-7 rounded-full bg-[var(--lala-blue)] text-white font-display flex items-center justify-center text-sm">{n}</span>
        <div className="font-display text-base text-[var(--lala-blue)]">{title}</div>
      </div>
      <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{children}</p>
    </div>
  );
}
